import { collection, onSnapshot, type Query, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { firestore } from '../services/firebase';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { Tender } from '../types/tender';

export const useTenders = ({ archived = false }: { archived?: boolean } = {}) => {
  const { account } = useAccount();
  const { showSnackbar } = useSnackbar();

  const [tenders, setTenders] = useState<Tender[]>([]);
  const [isLoadingTenders, setIsLoadingTenders] = useState(true);
  const [tendersError, setTendersError] = useState<string | null>(null);

  const isManager =
    account?.cachedPermissions?.includes(PERMISSIONS.TENDER_VIEW_CLIENT) ||
    account?.cachedPermissions?.includes(PERMISSIONS.TENDER_MANAGE_TEAM);

  useEffect(() => {
    if (!account?.uid || !account?.clientId) {
      setTenders([]);
      setIsLoadingTenders(false);
      return;
    }

    setIsLoadingTenders(true);
    setTendersError(null);
    const tendersRef = collection(firestore, 'tenders');

    let ownedOrManagedQ: Query;

    if (isManager) {
      if (
        account.cachedPermissions?.includes(PERMISSIONS.TENDER_MANAGE_TEAM) &&
        !account.cachedPermissions?.includes(PERMISSIONS.TENDER_VIEW_CLIENT) &&
        account.teamId
      ) {
        ownedOrManagedQ = query(
          tendersRef,
          where('clientId', '==', account.clientId),
          where('teamId', '==', account.teamId),
          where('archived', '==', archived)
        );
      } else {
        ownedOrManagedQ = query(
          tendersRef,
          where('clientId', '==', account.clientId),
          where('archived', '==', archived)
        );
      }
    } else {
      ownedOrManagedQ = query(
        tendersRef,
        where('clientId', '==', account.clientId),
        where('archived', '==', archived),
        where('ownerId', '==', account.uid)
      );
    }

    const sharedQ = query(
      tendersRef,
      where('archived', '==', archived),
      where('sharedWith', 'array-contains', account.uid)
    );

    let ownedOrManagedTenders: Tender[] = [];
    let sharedTenders: Tender[] = [];

    const mergeTenders = () => {
      const map = new Map<string, Tender>();

      for (const t of ownedOrManagedTenders) {
        map.set(t.id || '', t);
      }
      for (const t of sharedTenders) {
        map.set(t.id || '', t);
      }

      setTenders(Array.from(map.values()));
      setIsLoadingTenders(false);
    };

    const unsubOwned = onSnapshot(
      ownedOrManagedQ,
      (snapshot) => {
        ownedOrManagedTenders = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as Tender[];
        mergeTenders();
      },
      (_error) => {
        // We don't stop loading here because shared might succeed
      }
    );

    const unsubShared = onSnapshot(
      sharedQ,
      (snapshot) => {
        sharedTenders = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as Tender[];
        mergeTenders();
      },
      (_error) => {
        setTendersError(`Failed to fetch tenders: ${_error.message}`);
        showSnackbar(`Error listening for tender updates: ${_error.message}`, 'error');
        setIsLoadingTenders(false); // Ensure spinner stops
      }
    );

    return () => {
      unsubOwned();
      unsubShared();
    };
  }, [
    account?.uid,
    account?.clientId,
    showSnackbar,
    isManager,
    account?.cachedPermissions,
    account?.teamId,
    archived,
  ]);

  return { tenders, isLoadingTenders, tendersError, isManager };
};
